/**
 * Pure helpers for Grafana-style threshold colouring.
 *
 * KPI cards and bar-chart bars accept an optional ``thresholds`` array
 * describing colour brackets keyed off the rendered value:
 *
 *   [{ value: 0,  color: "#73BF69" },
 *    { value: 80, color: "#FF9830" },
 *    { value: 95, color: "#F2495C" }]
 *
 * meaning value <80 → green, 80..<95 → amber, ≥95 → red. The highest
 * threshold the value is at-or-above wins. Out-of-range values fall
 * to the lowest bracket (and ultimately to the renderer's fallback
 * when the array itself is empty / absent).
 */

import type { ColorThreshold } from "../dashboard/catalog"

/**
 * Pick the colour for ``value`` from ``thresholds``. Returns ``null``
 * when no threshold applies — caller falls back to its own accent.
 *
 * Implementation notes:
 * - Sorts a defensive copy ascending by ``value`` so author order
 *   doesn't matter.
 * - Returns the colour of the LAST entry whose value <= input.
 * - For inputs below every threshold, returns the FIRST colour (so a
 *   "0% green" baseline still surfaces when the value is missing /
 *   negative).
 */
export function pickThresholdColor(
  value: unknown,
  thresholds: ReadonlyArray<ColorThreshold> | undefined
): string | null {
  if (!Array.isArray(thresholds) || thresholds.length === 0) return null
  const numeric = coerceNumeric(value)
  if (numeric === null) return null

  const sorted = [...thresholds].sort((a, b) => a.value - b.value)
  let chosen: string | null = sorted[0]?.color ?? null
  for (const t of sorted) {
    if (numeric >= t.value) chosen = t.color
    else break
  }
  return chosen
}

/**
 * Match the renderer's value-coercion rules so a string-typed numeric
 * KPI ("541 524 042" / "100.39") still triggers thresholding. Mirrors
 * ``formatKpiValue`` in registry.tsx — strings with locale separators
 * parse cleanly; non-numeric strings (e.g. "Q3 2026") return null and
 * the caller skips threshold colouring.
 */
function coerceNumeric(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value !== "string") return null
  const stripped = value.replace(/[\s ]/g, "").replace(/,/g, ".")
  if (!stripped.match(/^-?\d+(\.\d+)?$/)) return null
  const n = Number(stripped)
  return Number.isFinite(n) ? n : null
}
